// 子設備加入（plan/vms-web-v0.md DEV-04；plan/subg-binding-v0.md §4.1、§8；protocol kinio_subg 0xFCE1）。
// 貼 QR 的子設備走 QR 白名單：掃 `KSG:1|e=<EUI>|s=<IS>|m=<model>` → app-api subg/pair → 閘道 kinio_subg.pair_start（180 秒）；
// permit_join 只給不需要 QR 的子設備。QR 解析與 component/protocol subg.go 同規則（向量：protocol testdata/subg_qr.json，本檔測試用同一組）。
import { kinioFetch, planeBase, idempotencyKey } from '@kinio/web-sdk';

export interface SubgQr {
  eui: string;
  is: string;
  model: string;
}

const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** EUI：16 個 hex（容忍 `:` / `-` / 空白、大小寫）→ 大寫無分隔；不合法回 null。 */
export function normalizeEui(s: string): string | null {
  const c = s.trim().replace(/[:\-\s]/g, '').toUpperCase();
  return /^[0-9A-F]{16}$/.test(c) ? c : null;
}

/** IS：RFC 4648 base32（容忍小寫、`=`、`-`、空白）→ 26 字元大寫；要剛好 16 bytes、尾端多餘位元為 0、不能全 0；不合法回 null。 */
export function normalizeIs(s: string): string | null {
  const c = s.trim().replace(/[=\-\s]/g, '').toUpperCase();
  if (c.length !== 26) return null;
  let bits = 0;
  let acc = 0;
  const out: number[] = [];
  for (const ch of c) {
    const v = B32.indexOf(ch);
    if (v < 0) return null;
    acc = (acc << 5) | v;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((acc >> bits) & 0xff);
      acc &= (1 << bits) - 1;
    }
  }
  if (out.length !== 16 || acc !== 0) return null; // 26 × 5 = 130 bits：多的 2 bits 要是 0（正規寫法）
  if (out.every((b) => b === 0)) return null; // 全 0 是 permit_join「沒有 IS」的代入值
  return c;
}

export type SubgQrError = 'not_subg' | 'version' | 'format' | 'eui' | 'is';

/** 解 `KSG:1|e=…|s=…|m=…`。 */
export function parseSubgQr(s: string): { ok: true; qr: SubgQr } | { ok: false; error: SubgQrError } {
  const parts = s.trim().split('|');
  const head = parts[0]?.toUpperCase() ?? '';
  if (head !== 'KSG:1') return { ok: false, error: head.startsWith('KSG:') ? 'version' : 'not_subg' };
  const kv = new Map<string, string>();
  for (const p of parts.slice(1)) {
    const i = p.indexOf('=');
    if (i <= 0) return { ok: false, error: 'format' };
    const k = p.slice(0, i).toLowerCase();
    if (kv.has(k)) return { ok: false, error: 'format' };
    kv.set(k, p.slice(i + 1));
  }
  if (!kv.has('e') || !kv.has('s')) return { ok: false, error: 'format' };
  const eui = normalizeEui(kv.get('e')!);
  if (!eui) return { ok: false, error: 'eui' };
  const is = normalizeIs(kv.get('s')!);
  if (!is) return { ok: false, error: 'is' };
  return { ok: true, qr: { eui, is, model: (kv.get('m') ?? '').trim() } };
}

export interface SubgJoin {
  mode?: 'none' | 'qr' | 'permit';
  until?: number;
  allowlist?: number;
}

export interface SubgChild {
  device_ref: string;
  index: number;
  name?: string;
  bound_at: string;
  protocol?: string | null;
  type_code?: string | null;
  model?: string | null;
  unique_id?: string | null;
  join_mode?: 'qr' | 'permit' | null;
}

export interface SubgStatus {
  gateway: string;
  supported: boolean;
  join?: SubgJoin | null;
  max_children?: number | null;
  region?: string | null;
  children: SubgChild[];
  can_manage: boolean;
}

export interface SubgWindow {
  status: 'pairing';
  mode: 'qr' | 'permit';
  eui?: string;
  model?: string | null;
  smartstart?: boolean;
  timeout_seconds: number;
  expires_at: string;
  join?: SubgJoin;
}

const devUrl = (api: string, homeId: string, device: string, path: string) =>
  `${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/vms/devices/${encodeURIComponent(device)}${path}`;

export const subgApi = {
  status: (api: string, homeId: string, gateway: string) => kinioFetch<SubgStatus>(devUrl(api, homeId, gateway, '/subg')),
  /** QR 白名單：{qr} 或 {eui, is}。一次操作一把 Idempotency-Key。 */
  pair: (api: string, homeId: string, gateway: string, body: { qr: string } | { eui: string; is: string; smartstart?: boolean }) =>
    kinioFetch<SubgWindow>(devUrl(api, homeId, gateway, '/subg/pair'), { method: 'POST', body, headers: { 'Idempotency-Key': idempotencyKey() } }),
  /** 只給不需要 QR 的子設備。 */
  permitJoin: (api: string, homeId: string, gateway: string, timeoutS?: number) =>
    kinioFetch<SubgWindow>(devUrl(api, homeId, gateway, '/subg/permit-join'), {
      method: 'POST',
      body: timeoutS ? { timeout_seconds: timeoutS } : {},
      headers: { 'Idempotency-Key': idempotencyKey() },
    }),
  stop: (api: string, homeId: string, gateway: string) => kinioFetch<{ status: string }>(devUrl(api, homeId, gateway, '/subg/stop'), { method: 'POST', body: {} }),
  /** 解除某個槽位的配對：閘道 kinio_subg.remove；平台等閘道回 kinio_subg.left 才解綁。 */
  remove: (api: string, homeId: string, gateway: string, index: number) =>
    kinioFetch<{ status: string; device_ref: string }>(devUrl(api, homeId, gateway, `/subg/children/${index}`), { method: 'DELETE' }),
  rename: (api: string, homeId: string, device: string, name: string) =>
    kinioFetch<{ device_ref: string; name: string }>(devUrl(api, homeId, device, '/name'), { method: 'PUT', body: { name } }),
};

export interface DeviceLocation {
  device_ref?: string;
  site_id?: string;
  location_version?: number;
}

/** 企業 home 的設備位置（既有 /v1/homes/{id}/devices/{device}/location；個人家戶沒有位置，用地圖擺放）。 */
export const locationApi = {
  get: (api: string, homeId: string, device: string) =>
    kinioFetch<DeviceLocation>(`${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/devices/${encodeURIComponent(device)}/location`),
  /** ORG-04 批次：一個企業 home 的多台位置同一交易指派（有一項不行整批拒絕，錯誤 detail 帶 index / device_ref）。 */
  assign: (api: string, homeId: string, items: { device_ref: string; site_id: string; expected_version: number }[]) =>
    kinioFetch<{ data: DeviceLocation[] }>(`${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/device-locations/assign`, {
      method: 'POST',
      body: { items },
    }),
  put: (api: string, homeId: string, device: string, siteId: string, expected: number) =>
    kinioFetch<DeviceLocation>(`${planeBase(api)}/v1/homes/${encodeURIComponent(homeId)}/devices/${encodeURIComponent(device)}/location`, {
      method: 'PUT',
      body: { site_id: siteId, expected_version: expected },
    }),
};

/** 加入窗口開著的時候，新出現的子設備 = 不在開窗前名單裡的；QR 模式另外要 EUI 對得上（同一時間別人加的不算這次）。 */
export function newChildren(before: Set<string>, now: SubgChild[], win: Pick<SubgWindow, 'mode' | 'eui'> | null): SubgChild[] {
  return now.filter((c) => !before.has(c.device_ref) && (!win || win.mode !== 'qr' || !win.eui || (c.unique_id ?? '').toUpperCase() === win.eui));
}
